import { Reducer } from 'react'
import { ValueOf } from 'type-fest'
import type { SlideConfig, WrappedData, WrappedTotalsData } from 'components/Pages/Wrapped/types/wrapped'

export enum WrappedStoryActions {
  togglePause = 'togglePause',
  toggleMute = 'toggleMute',
  setCurrentSlide = 'setCurrentSlide',
  setVideoReady = 'setVideoReady',
  setShareMenuOpen = 'setShareMenuOpen',
  setData = 'setData',
  setTotalsData = 'setTotalsData',
  setTotalsSlides = 'setTotalsSlides',
  setPhase = 'setPhase',
  setScrolled = 'setScrolled',
}

export interface WrappedStoryState {
  /** The current slide index */
  currentIndex: number
  /** Whether the story is paused */
  isPaused: boolean
  /** Whether the audio is muted */
  isMuted: boolean
  /** Whether the current video is ready to play */
  isVideoReady: boolean
  /** Whether the share menu is open */
  isShareMenuOpen: boolean
  /** The wrapped data from API (undefined = totals-only mode) */
  data: WrappedData | undefined
  /** The totals data from API for fallback display */
  totalsData: WrappedTotalsData | undefined
  /** List of active slides based on data conditions */
  slides: Array<SlideConfig>
  /** The current slide configuration */
  currentSlide: SlideConfig | undefined
  /** The phase, if set to question will stop timer */
  phase: 'question' | undefined
  /** Whether the current slide content is scrolled */
  isScrolled: boolean
}

export const INITIAL_WRAPPED_STORY_STATE: WrappedStoryState = {
  currentIndex: 0,
  isPaused: false,
  isMuted: true,
  isVideoReady: false,
  isShareMenuOpen: false,
  data: undefined,
  totalsData: undefined,
  slides: [],
  currentSlide: undefined,
  phase: undefined,
  isScrolled: false,
}

export type WrappedStoryDispatchAction = ValueOf<Utils.FullActionMap<{
  [WrappedStoryActions.togglePause]: {
    isPaused?: boolean
  }
  [WrappedStoryActions.toggleMute]: {
    isMuted?: boolean
  }
  [WrappedStoryActions.setCurrentSlide]: {
    index: number
  }
  [WrappedStoryActions.setVideoReady]: {
    isVideoReady: boolean
  }
  [WrappedStoryActions.setShareMenuOpen]: {
    isShareMenuOpen: boolean
  }
  [WrappedStoryActions.setData]: {
    data: WrappedData
    slides: Array<SlideConfig>
  }
  [WrappedStoryActions.setTotalsData]: {
    totalsData: WrappedTotalsData
  }
  [WrappedStoryActions.setTotalsSlides]: {
    slides: Array<SlideConfig>
    totalsData: WrappedTotalsData
  }
  [WrappedStoryActions.setPhase]: {
    phase: WrappedStoryState['phase']
  }
  [WrappedStoryActions.setScrolled]: {
    isScrolled: boolean
  }
}>>

const wrappedStoryStateReducer: Reducer<WrappedStoryState, WrappedStoryDispatchAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case WrappedStoryActions.togglePause: {
      const newIsPaused = action.isPaused !== undefined ? action.isPaused : !state.isPaused
      return {
        ...state,
        isPaused: newIsPaused,
      }
    }
    case WrappedStoryActions.toggleMute: {
      const newIsMuted = action.isMuted !== undefined ? action.isMuted : !state.isMuted
      return {
        ...state,
        isMuted: newIsMuted,
      }
    }
    case WrappedStoryActions.setCurrentSlide: {
      const newIndex = Math.max(0, Math.min(action.index, state.slides.length - 1))
      return {
        ...state,
        currentIndex: newIndex,
        currentSlide: state.slides[newIndex],
        isScrolled: false, // Reset scroll state when changing slides
        phase: undefined, // Reset phase when changing slides to ensure timer resumes
      }
    }
    case WrappedStoryActions.setVideoReady: {
      return {
        ...state,
        isVideoReady: action.isVideoReady,
      }
    }
    case WrappedStoryActions.setShareMenuOpen: {
      return {
        ...state,
        isShareMenuOpen: action.isShareMenuOpen,
        // Auto-pause when share menu opens
        isPaused: action.isShareMenuOpen ? true : state.isPaused,
      }
    }
    case WrappedStoryActions.setData: {
      return {
        ...state,
        data: action.data,
        slides: action.slides,
        currentSlide: action.slides[state.currentIndex],
      }
    }
    case WrappedStoryActions.setTotalsData: {
      return {
        ...state,
        totalsData: action.totalsData,
      }
    }
    case WrappedStoryActions.setTotalsSlides: {
      // Only set slides if we don't have user data (totals-only mode)
      if (state.data) return state
      return {
        ...state,
        slides: action.slides,
        currentSlide: action.slides[state.currentIndex],
        totalsData: action.totalsData,
      }
    }
    case WrappedStoryActions.setPhase: {
      return {
        ...state,
        phase: action.phase,
      }
    }
    case WrappedStoryActions.setScrolled: {
      return {
        ...state,
        isScrolled: action.isScrolled,
      }
    }
    default:
      return state
  }
}

export default wrappedStoryStateReducer
